// Функциональные стили виджета (CLAUDE.md § Дизайн). Живут внутри Shadow DOM,
// наружу не протекают. Палитра — в переменных на .root; базовая тема СВЕТЛАЯ,
// тёмную включает класс .w-theme-dark, который ставит виджет по теме САЙТА
// (настройка владельца → data-theme хоста → яркость фона, см. theme.ts).
// Системной prefers-color-scheme здесь нет намеренно: тёмная ОС гостя не делает
// светлый сайт клиента тёмным. Акцент — бренд-фиолетовый.
// prefers-reduced-motion отключает единственную анимацию («печатает…») — §3.
export const WIDGET_CSS = `
:host {
  all: initial;
}
* {
  box-sizing: border-box;
  margin: 0;
  padding: 0;
}
.root {
  --w-bg: #ffffff;
  --w-surface: #f9fafb;
  --w-text: #1f2937;
  --w-muted: #6b7280;
  --w-border: #e5e7eb;
  --w-input-border: #d1d5db;
  --w-accent: #7b6cf6;
  --w-accent-hover: #6a58ee;
  /* Акцент для ТЕКСТА, а не для заливки: #7b6cf6 на светлой карточке даёт
     3.8:1 — ниже AA 4.5:1 для обычного текста. Тёмная версия того же
     фиолетового держит 5.9:1 (WCAG 2.1 §1.4.3). */
  --w-accent-text: #5b4bd6;
  --w-msg-bg: #f3f4f6;
  --w-ok-bg: #ecfdf5;
  --w-ok-text: #065f46;
  --w-warn-bg: #fef3c7;
  --w-warn-text: #92400e;
  --w-warn-border: #fde68a;
  --w-green-text: #17b877;
  --w-green-bg: rgba(23, 184, 119, 0.12);
  position: fixed;
  right: 20px;
  bottom: 20px;
  z-index: 2147483000;
  font-family: system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif;
  font-size: 15px;
  line-height: 1.4;
  color: var(--w-text);
}
/* Открытый чат — поверх всего на странице. Чужой виджет с z-index выше нашего
   (Callibri у клиники «Энигма», 2147483646) накрывал кнопку «Отправить» на
   телефоне. Закрытый пузырь остаётся на 2147483000 и не лезет поверх модалок сайта. */
.root.w-open {
  z-index: 2147483647;
}
/* Тёмная палитра: класс ставит applyHostTheme() по теме САЙТА (theme.ts), в том
   числе на лету — MutationObserver следит за data-theme хоста (SPA). */
.root.w-theme-dark {
  --w-bg: #16151f;
  --w-surface: #1c1b28;
  --w-text: #f2f2f7;
  --w-muted: rgba(242, 242, 247, 0.62);
  --w-border: rgba(255, 255, 255, 0.10);
  --w-input-border: rgba(255, 255, 255, 0.18);
  --w-accent: #7b6cf6;
  --w-accent-hover: #8b7cff;
  /* На тёмной панели наоборот — светлее: 7.1:1. */
  --w-accent-text: #a99bff;
  --w-msg-bg: #26243a;
  --w-ok-bg: rgba(31, 184, 119, 0.16);
  --w-ok-text: #4fd39a;
  --w-warn-bg: rgba(245, 181, 88, 0.14);
  --w-warn-text: #f0b34a;
  --w-warn-border: rgba(245, 181, 88, 0.30);
  --w-green-text: #4fd39a;
  --w-green-bg: rgba(31, 184, 119, 0.16);
}
.bubble {
  position: relative;
  width: 56px;
  height: 56px;
  border: none;
  border-radius: 50%;
  background: var(--w-accent);
  color: #fff;
  font-size: 24px;
  cursor: pointer;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.25);
  display: flex;
  align-items: center;
  justify-content: center;
}
.bubble:hover {
  background: var(--w-accent-hover);
}
/* Иконка лаунчера спокойно «дышит» прозрачностью — индикатор «на связи», не
   мигалка. Анимация CSS (не таймер): свёрнутая вкладка тормозит её сама, а
   prefers-reduced-motion гасит целиком (§5). */
.bubble-icon {
  display: block;
  line-height: 1;
}
@media (prefers-reduced-motion: no-preference) {
  .bubble-icon {
    animation: widget-bubble-breathe 2.6s ease-in-out infinite;
  }
}
@keyframes widget-bubble-breathe {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
}
/* Бейдж непрочитанных на свёрнутом пузыре: счётчик ответов, пока чат свёрнут
   или вкладка неактивна; сбрасывается открытием чата. */
.badge {
  position: absolute;
  top: -4px;
  right: -4px;
  min-width: 20px;
  height: 20px;
  padding: 0 5px;
  border-radius: 999px;
  background: #dc2626;
  color: #fff;
  font-size: 11.5px;
  font-weight: 700;
  line-height: 20px;
  text-align: center;
  box-shadow: 0 0 0 2px var(--w-bg);
}
@media (prefers-reduced-motion: no-preference) {
  .badge {
    animation: widget-pop 0.25s ease;
  }
}
@keyframes widget-pop {
  from {
    transform: scale(0.5);
  }
  to {
    transform: scale(1);
  }
}
/* Тизер-приглашение у лаунчера (виджет-паритет, волна 2): бабл над кнопкой с
   текстом-приглашением и крестиком «скрыть». Появление — только без
   prefers-reduced-motion (§5). */
.teaser {
  position: absolute;
  right: 0;
  bottom: 70px;
  display: flex;
  align-items: flex-start;
  gap: 8px;
  width: 240px;
  padding: 12px 14px;
  background: var(--w-bg);
  border: 1px solid var(--w-border);
  border-radius: 14px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.18);
}
@media (prefers-reduced-motion: no-preference) {
  /* Всплытие — только у СТАТИЧНОГО тизера (без data-phase). У печатающего
     появлением заведует сама фаза: иначе пузырь сначала всплыл бы пустым,
     потом погас на паузу и всплыл второй раз — мигание на ровном месте. */
  .teaser:not([data-phase]) {
    animation: widget-teaser-in 0.3s ease;
  }
}
@keyframes widget-teaser-in {
  from {
    opacity: 0;
    transform: translateY(8px);
  }
  to {
    opacity: 1;
    transform: none;
  }
}
/* Фазы цикла фраз (data-phase ставит widget.ts, тайминги — teaser.ts).
   Пауза пустоты — не пустой пузырь, а погашенный целиком: рамка с одним
   крестиком полминуты выглядела бы поломкой. pointer-events снимаем, чтобы
   невидимая плашка не перехватывала клики по странице под ней. */
.teaser[data-phase='idle'] {
  opacity: 0;
  pointer-events: none;
}
@media (prefers-reduced-motion: no-preference) {
  .teaser[data-phase='typing'] {
    transition: opacity 0.35s ease;
  }
  /* Уход: два моргания, потом затухание. Длительность обязана совпадать с
     LEAVE_MS в teaser.ts. forwards — держим погасшим до смены фазы на idle. */
  .teaser[data-phase='leaving'] {
    animation: widget-teaser-leave 1400ms ease-in-out forwards;
  }
}
@keyframes widget-teaser-leave {
  0%,
  24%,
  48% {
    opacity: 1;
  }
  12%,
  36% {
    opacity: 0.15;
  }
  100% {
    opacity: 0;
  }
}
.teaser-text {
  flex: 1;
  min-width: 0;
  border: none;
  background: none;
  margin: 0;
  padding: 0;
  font: inherit;
  font-size: 13.5px;
  line-height: 1.4;
  color: var(--w-text);
  text-align: left;
  overflow-wrap: anywhere;
  cursor: pointer;
}
/* Печатающиеся фразы: невидимый близнец самой длинной фразы и сама строка лежат
   в ОДНОЙ ячейке сетки. Высота пузыря = высота самой длинной фразы, поэтому он
   не прыгает на каждой букве; а если строка окажется выше близнеца (широкие
   глифы при том же числе букв), пузырь вырастет, а не разъедется. */
.teaser-stack {
  display: grid;
}
.teaser-sizer,
.teaser-line {
  grid-area: 1 / 1;
}
.teaser-sizer {
  visibility: hidden;
}
.teaser-close {
  flex: none;
  border: none;
  background: none;
  color: var(--w-muted);
  font-size: 14px;
  line-height: 1;
  padding: 0 2px;
  cursor: pointer;
}
.teaser-close:hover {
  color: var(--w-text);
}
/* Окно чата стоит над пузырём, но не выше 20px от верха окна браузера. Пузырь,
   поднятый настройкой (--w-offset-bottom ставит placeRoot), уносил шапку с
   крестиком за верх экрана: «Энигма», отступ 264, ноутбук с окном 650 px —
   шапка на 154 px выше края, закрыть чат нечем (диалог №7012, 15.10.2026).
   Окно опускается ровно на столько, сколько не влезает; при отступе 20 сдвиг 0. */
.panel {
  position: absolute;
  right: 0;
  bottom: calc(
    -1 *
      clamp(
        0px,
        var(--w-offset-bottom, 20px) + min(540px, 100vh - 110px) + 20px - 100vh,
        var(--w-offset-bottom, 20px) - 20px
      )
  );
  width: 360px;
  height: 540px;
  max-height: calc(100vh - 110px);
  display: flex;
  flex-direction: column;
  background: var(--w-bg);
  border: 1px solid var(--w-border);
  border-radius: 12px;
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.18);
  overflow: hidden;
}
.panel.hidden {
  display: none;
}
.header {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 14px;
  background: var(--w-surface);
  border-bottom: 1px solid var(--w-border);
}
/* Аватар+имя ведут к тому же боту в MAX/Telegram (config.header_bot_url): кому
   удобнее в мессенджере — уходит туда одним тапом (site_chat.css:138-151). Без
   URL это тот же контейнер обычным <div> (не ссылка, без cursor/hover). */
.identity {
  display: flex;
  flex: 1;
  align-items: center;
  gap: 10px;
  min-width: 0;
  color: inherit;
  text-decoration: none;
}
a.identity {
  cursor: pointer;
}
a.identity:hover {
  opacity: 0.85;
}
/* Обёртка аватара — якорь для пульсирующей точки «онлайн». */
.avatar-wrap {
  position: relative;
  flex-shrink: 0;
}
.avatar,
.avatar-fallback {
  width: 44px;
  height: 44px;
  border-radius: 50%;
  flex-shrink: 0;
}
.avatar {
  object-fit: cover;
  background: var(--w-surface);
  display: block;
}
.avatar-fallback {
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--w-accent);
  color: #fff;
  font-weight: 600;
  font-size: 18px;
}
/* Пульсирующая зелёная точка «онлайн» на аватаре (site_chat.css:194-210); пульс
   только без prefers-reduced-motion (§5: анимация = движение). Кольцо-бордюр под
   цвет шапки, чтобы точка «сидела» на аватаре. */
.avatar-dot {
  position: absolute;
  right: 0;
  bottom: 0;
  width: 11px;
  height: 11px;
  border: 2px solid var(--w-surface);
  border-radius: 50%;
  background: var(--w-green-text);
}
@media (prefers-reduced-motion: no-preference) {
  .avatar-dot {
    animation: widget-pulse 2s ease-in-out infinite;
  }
}
@keyframes widget-pulse {
  0%,
  100% {
    box-shadow: 0 0 0 0 rgba(23, 184, 119, 0.5);
  }
  50% {
    box-shadow: 0 0 0 5px rgba(23, 184, 119, 0);
  }
}
.who {
  flex: 1;
  min-width: 0;
}
.who .name {
  font-weight: 600;
}
.who .role {
  font-size: 12.5px;
  color: var(--w-muted);
}
/* Подзаголовок «Обычно отвечает за пару минут» под именем (site_chat.css:217-221). */
.who .subtitle {
  font-size: 12px;
  color: var(--w-muted);
}
/* Подпись платформы у бесплатного сотрудника: мелко и приглушённо, вне ленты —
   не загораживает переписку и не уезжает с прокруткой. */
.branding {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 12px 8px;
  font-size: 11px;
  color: var(--w-muted);
}
.branding-link {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  color: inherit;
  text-decoration: none;
}
.branding-link:hover {
  text-decoration: underline;
}
.branding img {
  height: 20px;
  width: auto;
  display: block;
}
.close,
.back {
  border: none;
  background: none;
  font-size: 20px;
  color: var(--w-muted);
  cursor: pointer;
  padding: 4px 8px;
}
/* Кнопка возврата к прежнему сотруднику: рядом с крестиком, но крупнее по
   области нажатия — на телефоне промах по ней означает потерянный разговор. */
.back {
  font-size: 18px;
  padding: 4px 6px;
}
.messages {
  flex: 1;
  overflow-y: auto;
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  background: var(--w-bg);
}
.msg {
  max-width: 82%;
  padding: 8px 12px;
  border-radius: 12px;
  white-space: pre-wrap;
  overflow-wrap: break-word;
}
/* Автоссылка в ответе (linkify.ts): цвет пузыря, подчёркивание — длинный
   адрес переносится, а не растягивает пузырь. */
.msg a {
  color: inherit;
  text-decoration: underline;
  text-underline-offset: 2px;
  overflow-wrap: anywhere;
}
.msg a:hover {
  opacity: 0.8;
}
.msg.visitor {
  align-self: flex-end;
  background: var(--w-accent);
  color: #fff;
  border-bottom-right-radius: 4px;
}
.msg.assistant {
  align-self: flex-start;
  background: var(--w-msg-bg);
  color: var(--w-text);
  border-bottom-left-radius: 4px;
}
/* Реплика ЖИВОГО человека: тот же угол, что у бота, но своя рамка и подпись
   сверху. Посетитель обязан понимать, кто ему отвечает — до 17.08.2026 ответ
   оператора выглядел ровно как ответ бота. */
.msg.operator {
  align-self: flex-start;
  background: var(--w-msg-bg);
  color: var(--w-text);
  border-bottom-left-radius: 4px;
  border-left: 3px solid var(--w-green-text);
}
.msg-who {
  align-self: flex-start;
  margin: 2px 0 -4px;
  font-size: 11.5px;
  font-weight: 600;
  letter-spacing: 0.02em;
  color: var(--w-green-text);
}
/* Плашка «к разговору подключился человек» — один раз на переход к оператору. */
.handoff-note {
  align-self: center;
  max-width: 90%;
  padding: 6px 12px;
  border-radius: 10px;
  background: var(--w-ok-bg);
  color: var(--w-ok-text);
  font-size: 12.5px;
  text-align: center;
}
.msg.local {
  align-self: center;
  background: var(--w-ok-bg);
  color: var(--w-ok-text);
  font-size: 13.5px;
}
/* Галочки доставки у пузыря посетителя: ✓ отправлено, ✓✓ ответ получен.
   На акцентном пузыре сигнал — контраст: доставленные ярче (как в marketpult). */
.ticks {
  display: inline-block;
  margin-left: 6px;
  line-height: 1;
  vertical-align: -1px;
  color: #fff;
  opacity: 0.55;
}
.ticks svg {
  display: block;
  overflow: visible;
  fill: none;
  stroke: currentcolor;
  stroke-width: 1.7;
  stroke-linecap: round;
  stroke-linejoin: round;
}
.ticks.delivered {
  opacity: 1;
}
.typing {
  align-self: flex-start;
  display: flex;
  gap: 4px;
  align-items: center;
  padding: 10px 12px;
  background: var(--w-msg-bg);
  border-radius: 12px;
  color: var(--w-muted);
  font-size: 13px;
}
/* Пауза внутри набора: дотс невидим, но строка в ленте остаётся — иначе
   каждое мигание пачки складывало и раскладывало разговор. */
.typing.hushed {
  opacity: 0;
}
.typing .dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: var(--w-muted);
}
@media (prefers-reduced-motion: no-preference) {
  .typing {
    transition: opacity 0.15s ease;
  }
  .typing .dot {
    animation: widget-blink 1.2s infinite both;
  }
  .typing .dot:nth-child(2) {
    animation-delay: 0.2s;
  }
  .typing .dot:nth-child(3) {
    animation-delay: 0.4s;
  }
}
@keyframes widget-blink {
  0%,
  80%,
  100% {
    opacity: 0.3;
  }
  40% {
    opacity: 1;
  }
}
.banner {
  padding: 8px 12px;
  font-size: 13px;
  background: var(--w-warn-bg);
  color: var(--w-warn-text);
  border-top: 1px solid var(--w-warn-border);
  display: flex;
  align-items: center;
  gap: 8px;
  justify-content: space-between;
}
.banner.hidden {
  display: none;
}
.banner button {
  border: 1px solid var(--w-input-border);
  background: var(--w-bg);
  color: var(--w-text);
  border-radius: 6px;
  padding: 3px 10px;
  font-size: 12.5px;
  cursor: pointer;
}
.composer {
  display: flex;
  align-items: flex-end;
  gap: 8px;
  padding: 10px;
  border-top: 1px solid var(--w-border);
  background: var(--w-bg);
}
/* Обёртка поля ввода — якорь для крестика очистки (site_chat.css:483-489). */
.input-wrap {
  position: relative;
  display: flex;
  flex: 1;
  min-width: 0;
}
.composer textarea {
  flex: 1;
  resize: none;
  border: 1px solid var(--w-input-border);
  border-radius: 8px;
  padding: 8px 32px 8px 10px; /* справа — место под крестик очистки */
  font: inherit;
  min-height: 40px;
  /* autosize (widget.ts) растит поле по scrollHeight до ~4 строк, дальше — скролл;
     фиксированной height нет — иначе длинный плейсхолдер переносился и обрезался. */
  max-height: 110px;
  overflow-y: auto;
  background: var(--w-bg);
  color: var(--w-text);
}
/* Контрастный плейсхолдер для ОБЕИХ тем (var(--w-muted) следует за темой);
   opacity:1 — Firefox иначе приглушает и «Сообщение…» бледнеет. */
.composer textarea::placeholder {
  color: var(--w-muted);
  opacity: 1;
}
.composer textarea:focus {
  outline: 2px solid var(--w-accent);
  border-color: transparent;
}
/* Крестик очистки набранного (site_chat.css:509-529): виден только при непустом
   поле (JS-тоггл .hidden), одно нажатие стирает всё сообщение — прежде всего
   предзаполненную фразу, которую иначе на телефоне выделять мучительно. */
.input-clear {
  position: absolute;
  right: 6px;
  bottom: 7px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  padding: 0;
  border: none;
  border-radius: 50%;
  background: none;
  color: var(--w-muted);
  font-size: 16px;
  line-height: 1;
  cursor: pointer;
}
.input-clear:hover {
  color: var(--w-text);
  background: var(--w-msg-bg);
}
/* Скрепка вложений: кнопка (доступна с клавиатуры) + скрытый file-input. */
.attach-wrap {
  align-self: center;
  flex: none;
  line-height: 0;
}
.attach {
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: transparent;
  border-radius: 8px;
  font-size: 16px;
  cursor: pointer;
  padding: 0;
}
.attach:hover {
  background: var(--w-msg-bg);
}
.attach:focus-visible {
  outline: 2px solid var(--w-accent);
}
/* Чипы выбранных файлов над композером — с ✕ удаления до отправки. */
.files {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  padding: 8px 10px 0;
  background: var(--w-bg);
}
.file-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  max-width: 100%;
  border: 1px solid var(--w-border);
  border-radius: 999px;
  padding: 3px 8px;
  font-size: 12px;
  background: var(--w-msg-bg);
  color: var(--w-text);
}
.file-chip-name {
  max-width: 150px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.file-chip-size {
  color: var(--w-muted);
  flex: none;
}
.file-chip-remove {
  border: none;
  background: transparent;
  color: var(--w-muted);
  font: inherit;
  font-size: 12px;
  line-height: 1;
  padding: 2px;
  cursor: pointer;
}
.file-chip-remove:hover {
  color: var(--w-text);
}
/* Вложения в пузырях ленты: file-link «📎 имя (размер)» и превью картинок. */
.msg-files {
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-top: 4px;
}
.file-line {
  font-size: 13px;
  overflow-wrap: anywhere;
}
.file-link {
  display: block;
  font-size: 13px;
  color: inherit;
  text-decoration: underline;
  overflow-wrap: anywhere;
}
.msg-image {
  display: block;
  line-height: 0;
}
.msg-image img {
  max-width: 180px;
  max-height: 140px;
  border-radius: 8px;
}
.btn {
  border: none;
  border-radius: 8px;
  background: var(--w-accent);
  color: #fff;
  padding: 8px 14px;
  font: inherit;
  cursor: pointer;
}
.btn:disabled {
  opacity: 0.5;
  cursor: default;
}
.btn.secondary {
  background: var(--w-bg);
  color: var(--w-text);
  border: 1px solid var(--w-input-border);
}
.lead-cta {
  align-self: flex-start;
  margin: 2px 0 0;
  border: 1px solid var(--w-accent);
  background: transparent;
  color: var(--w-accent);
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  padding: 7px 14px;
  border-radius: 999px;
}
.lead-cta:hover {
  background: var(--w-accent);
  color: #fff;
}
/* Инлайн-форма контакта в ленте чата (виджет-паритет, волна 2): блок в потоке
   сообщений — чат остаётся виден и прокручиваем, экран не заменяется. */
.lead-form-inline {
  display: flex;
  flex-direction: column;
  gap: 9px;
  align-self: stretch;
  margin: 4px 0 2px;
  padding: 13px;
  border: 1px solid var(--w-border);
  border-radius: 12px;
  background: var(--w-surface);
}
.lead-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
}
.lead-title {
  font-size: 13.5px;
  font-weight: 600;
  color: var(--w-text);
}
/* Крестик «закрыть форму, продолжить чат» (skip) — диалог не теряется.
   Зона тапа 44×44 (WCAG 2.5.5 / Apple HIG): пальцем в прежние 19×23 попадали
   через раз. Отрицательные поля гасят её ВНЕШНЕ — шапка формы остаётся той же
   высоты, крестик визуально стоит там же, где стоял. */
.lead-close {
  flex: none;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  margin: -12px -13px -12px 0;
  border: none;
  background: none;
  color: var(--w-muted);
  font-size: 16px;
  line-height: 1;
  padding: 0;
  cursor: pointer;
}
.lead-close:hover {
  color: var(--w-text);
}
/* Путь без анкеты: «Продолжить в Telegram/MAX» кнопками первыми. */
.lead-messengers {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}
.lead-messenger {
  border: 1px solid var(--w-accent);
  background: transparent;
  color: var(--w-accent);
  font: inherit;
  font-size: 12.5px;
  font-weight: 500;
  padding: 7px 12px;
  border-radius: 999px;
  cursor: pointer;
}
.lead-messenger:hover {
  background: var(--w-accent);
  color: #fff;
}
.lead-or {
  font-size: 11.5px;
  color: var(--w-muted);
}
.lead-field {
  display: flex;
  flex-direction: column;
  gap: 3px;
}
.lead-label {
  font-size: 12px;
  color: var(--w-muted);
}
/* Звёздочка обязательного поля: видно, что без него заявку не отправить.
   Читалке экрана её не объявляем — там за обязательность отвечает
   aria-required на самом поле, иначе поле объявляется дважды. */
.lead-required {
  color: var(--w-accent);
  margin-left: 2px;
}
.lead-field input,
.lead-field textarea {
  border: 1px solid var(--w-input-border);
  border-radius: 8px;
  padding: 8px 10px;
  font: inherit;
  background: var(--w-bg);
  color: var(--w-text);
}
.lead-field input:focus,
.lead-field textarea:focus {
  outline: 2px solid var(--w-accent);
  border-color: transparent;
}
/* Невалидное поле — красная обводка + строка ошибки под ним (живая валидация). */
.lead-field input.invalid {
  border-color: #e05252;
  outline-color: #e05252;
}
.lead-field-error {
  font-size: 11.5px;
  color: #e05252;
}
.lead-error {
  color: #e05252;
  font-size: 12.5px;
  min-height: 16px;
}
.lead-submit {
  align-self: flex-start;
}
.loading {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--w-muted);
  font-size: 14px;
}
.hidden {
  display: none;
}
/* Атрибут hidden обязан прятать ЛЮБОЙ узел: браузерный дефолт (display: none)
   перебивается собственным display класса — так чипы «Продолжить в …»
   (.quick — flex) оставались бы на экране с hidden. */
[hidden] {
  display: none !important;
}
/* Витрина сотрудников: горизонтальная лента карточек (Assistants::Scenario). */
.roster {
  display: flex;
  /* flex: none обязателен — см. .gallery: лента прокрутки в колоночном флексе
     схлопывается в ноль, как только диалог перестаёт влезать в панель. */
  flex: none;
  gap: 10px;
  overflow-x: auto;
  padding: 2px 0 6px;
  scroll-snap-type: x mandatory;
}
.roster-card {
  flex: 0 0 132px;
  scroll-snap-align: start;
  display: flex;
  flex-direction: column;
  background: var(--w-surface);
  border: 1px solid var(--w-border);
  border-radius: 12px;
  overflow: hidden;
}
.roster-photo {
  position: relative;
  aspect-ratio: 1 / 0.92;
  background: var(--w-msg-bg);
}
.roster-photo img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: top;
}
.roster-online {
  position: absolute;
  left: 6px;
  bottom: 6px;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 10px;
  font-weight: 600;
  color: var(--w-green-text);
  background: var(--w-green-bg);
  padding: 2px 6px;
  border-radius: 999px;
}
.roster-online::before {
  content: '';
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background: var(--w-green-text);
}
.roster-body {
  display: flex;
  flex-direction: column;
  flex: 1;
  padding: 8px 9px 10px;
}
.roster-name {
  font-size: 12.5px;
  font-weight: 600;
}
.roster-role {
  font-size: 10.5px;
  color: var(--w-accent);
  margin-top: 1px;
  line-height: 1.3;
}
.roster-desc {
  font-size: 10.5px;
  color: var(--w-muted);
  margin-top: 5px;
  line-height: 1.35;
  flex: 1;
}
.roster-honesty {
  font-size: 9.5px;
  color: var(--w-muted);
  font-style: italic;
  margin-top: 6px;
  line-height: 1.35;
}
.roster-hire {
  margin-top: 8px;
  text-align: center;
  text-decoration: none;
  border: 1px solid var(--w-accent);
  color: var(--w-accent);
  font-size: 11.5px;
  font-weight: 600;
  padding: 6px 0;
  border-radius: 8px;
  cursor: pointer;
}
.roster-hire:hover {
  background: var(--w-accent);
  color: #fff;
}
/* Фото-карточки работ владельца (блок 'gallery', маркер [[галерея]]): лента
   миниатюр с подписями. Скролл живёт ВНУТРИ ленты (панель всего 360px, у неё
   overflow:hidden) — страница хоста горизонтально не съезжает. Палитра — те же
   переменные, поэтому светлая и тёмная темы получаются сами. */
.gallery {
  display: flex;
  /* НЕ УБИРАТЬ. Лента — контейнер прокрутки (overflow-x), а у такого элемента
     автоминимум (min-height: auto) равен НУЛЮ, а не высоте содержимого. Внутри
     .messages (колоночный флекс со скроллом) это значит: пока диалог влезает в
     панель — карточки видны, а как только перестаёт — флекс сжимает ленту в
     ноль, и вместо фото остаётся полоска рамок. Ровно так фото и «исчезали» в
     длинном диалоге на телефоне: блок доставлен, картинки загружены, высота 2px. */
  flex: none;
  gap: 10px;
  min-width: 0;
  max-width: 100%;
  overflow-x: auto;
  overscroll-behavior-x: contain;
  padding: 2px 0 6px;
  scroll-snap-type: x mandatory;
}
.qr {
  /* flex: none — та же мина, что у ленты работ: в колоночном флексе со скроллом
     автоминимум равен нулю, и картинка схлопнулась бы в полоску. */
  flex: none;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  margin: 2px 0 6px;
}
.qr-image {
  display: block;
  /* Код наводят камерой с экрана: 200px — минимум, при котором телефон читает
     его уверенно в панели шириной 360px. */
  width: 200px;
  max-width: 100%;
  border-radius: 12px;
  overflow: hidden;
  background: #fff;
  padding: 8px;
  border: 1px solid var(--w-border);
}
.qr-image img {
  display: block;
  width: 100%;
  /* ⚠️ aspect-ratio, а не height: auto. Код приходит SVG'шкой с одним viewBox
     и без собственных размеров: в колоночном флексе такая картинка получает
     высоту РОВНО НОЛЬ (проверено на живом сайте — блок доставлен, ширина 182px,
     высота 0). Квадрат задаём сами: QR по определению квадратный. */
  height: auto;
  aspect-ratio: 1 / 1;
}
.qr-note {
  max-width: 260px;
  text-align: center;
  font-size: 12px;
  line-height: 1.4;
  color: var(--w-muted);
}
.gallery-card {
  /* 220px, а не 168: при 168 фото занимало 56% карточки, а подпись владельца
     («99 м² · 250 мм · М300, ЭППС и тёплый пол · 17 дней · Калининград ·
     970 000 ₽») разваливалась на четыре строки мелкого серого текста. Шире —
     фото читается как фото, а подпись укладывается в две-три строки. */
  flex: 0 0 220px;
  scroll-snap-align: start;
  display: flex;
  flex-direction: column;
  background: var(--w-surface);
  border: 1px solid var(--w-border);
  border-radius: 12px;
  overflow: hidden;
  color: inherit;
  text-decoration: none;
}
.gallery-card:hover {
  border-color: var(--w-accent);
}
.gallery-card:focus-visible {
  outline: 2px solid var(--w-accent);
  outline-offset: 1px;
}
.gallery-card img {
  display: block;
  width: 100%;
  aspect-ratio: 4 / 3;
  object-fit: cover;
  background: var(--w-msg-bg);
}
.gallery-body {
  padding: 10px 11px 12px;
}
.gallery-title {
  font-size: 13px;
  font-weight: 600;
  line-height: 1.35;
}
.gallery-meta {
  font-size: 11.5px;
  color: var(--w-muted);
  margin-top: 5px;
  line-height: 1.45;
}
/* Цена — отдельной строкой и акцентом: клиент ищет в карточке именно её, а в
   общей строке через «·» она стояла последней и терялась. Значение берётся из
   подписи владельца КАК ЕСТЬ (gallery.ts splitPrice) — ничего не считаем. */
.gallery-price {
  margin-top: 6px;
  font-size: 13px;
  font-weight: 700;
  color: var(--w-accent-text);
}
/* Карточки товара (блок 'shop', маркер [[товары:…]]) — та же лента, что у
   работ, плюс ряд кнопок. Отдельный класс, а не копия ленты: витрина у них
   общая, разница только в подвале карточки. */
.shop-card {
  /* Шире работ (220px): карточка несёт фото, название И кнопки, а картинки
     в каталоге разной формы — обложка книги 2:3, картинка истории 16:9.
     250px — предел, при котором карточка целиком (фото + подпись + кнопка)
     ещё влезает в ленту сообщений панели: замерено на живом сайте 18.08.2026,
     343px карточки против 362px ленты. */
  flex: 0 0 250px;
  /* Подпись прижата к своей картинке, а не размазана по свободному месту:
     space-between уводил её на середину карточки у горизонтальной картинки,
     когда рядом в ряду стояла высокая обложка. Кнопки держит margin-top
     у .shop-actions — они по-прежнему на одной линии во всём ряду. */
  justify-content: flex-start;
}
/* Товар показываем ЦЕЛИКОМ, а не кадрируем. У работ кадрировать можно — там
   объект в кадре и так, — а фото шара снято вертикально на белом, и cover
   срезал ему бока и хвостик (живой сайт 06.08.2026). Квадрат вместо 4:3 по той
   же причине: вертикальный снимок в широкой рамке оставляет пустые поля.

   Поля заливаем фоном САМОЙ карточки, а не белым. Белый был незаметен, пока
   каталог состоял из фото на белом фоне; книжная обложка 2:3 и картинка
   истории 16:9 получали от него яркую рамку посреди тёмного виджета
   (живой сайт 18.08.2026). Тем же цветом поля просто исчезают, а фото на
   белом фоне по-прежнему сливается со светлой темой. */
.shop-card img {
  /* Коробка под картинку принимает ЕЁ пропорции, а не квадрат. Квадрат был
     компромиссом на каталоге из одинаковых фото товара; на смешанном он
     обворовывал обе стороны — вертикальная обложка 2:3 ужималась до 147px
     ширины в карточке шириной 220, а горизонтальная 16:9 висела в пустоте
     с полями по 48px сверху и снизу (живой сайт 18.08.2026).
     Потолок высоты нужен вертикальным: без него обложка съедала бы панель
     целиком, и до кнопки «Открыть» пришлось бы листать. */
  width: 100%;
  height: auto;
  display: block;
  max-height: 260px;
  min-height: 110px;
  object-fit: contain;
  background: var(--w-surface);
}
.shop-photo {
  display: block;
  color: inherit;
  text-decoration: none;
}
.shop-photo:focus-visible {
  outline: 2px solid var(--w-accent);
  outline-offset: -2px;
}
.shop-actions {
  display: flex;
  gap: 6px;
  padding: 0 11px 11px;
  /* Кнопки на одной линии во всём ряду: карточки тянутся до самой высокой,
     и без этого ряд кнопок гулял бы по высоте от карточки к карточке. */
  margin-top: auto;
}
.shop-checkout {
  display: flex;
  flex: none;
  justify-content: flex-end;
  padding: 0 0 6px;
}
.shop-btn {
  flex: 1 1 auto;
  padding: 7px 10px;
  border: 1px solid var(--w-border);
  border-radius: 9px;
  background: transparent;
  color: var(--w-text);
  font: inherit;
  font-size: 12px;
  font-weight: 600;
  line-height: 1.2;
  text-align: center;
  text-decoration: none;
  cursor: pointer;
}
.shop-btn.primary {
  border-color: var(--w-accent);
  background: var(--w-accent);
  color: #fff;
}
.shop-btn.primary:hover {
  background: var(--w-accent-hover);
}
/* Положили — кнопка перестаёт звать и становится отметкой: повторный клик
   ничего не добавит, а зелёная плашка отвечает на «а точно добавилось?». */
.shop-btn.done {
  border-color: transparent;
  background: var(--w-ok-bg);
  color: var(--w-ok-text);
  cursor: default;
}
.shop-btn:disabled {
  opacity: 0.75;
}
.shop-btn:focus-visible {
  outline: 2px solid var(--w-accent);
  outline-offset: 1px;
}
/* Подтверждённый заказ в корзине САЙТА (message.cart, Orders::Cart): компактный
   список с отметками и «Оформить заказ» под ним. Кладёт виджет, оформляет
   человек. flex: none — та же мина, что у лент: без него блок схлопывается. */
.cart-sync {
  flex: none;
  margin: 2px 0 8px;
  padding: 10px 12px;
  border: 1px solid var(--w-border);
  border-radius: 12px;
  background: var(--w-surface);
  font-size: 12.5px;
  line-height: 1.35;
}
.cart-sync-title {
  margin-bottom: 6px;
  font-weight: 700;
}
.cart-row {
  display: flex;
  gap: 8px;
  align-items: baseline;
  padding: 2px 0;
}
.cart-row-mark {
  flex: none;
  width: 16px;
  text-align: center;
  color: var(--w-muted);
}
.cart-row.ok .cart-row-mark {
  color: var(--w-ok-text);
}
.cart-row.failed .cart-row-title {
  color: var(--w-muted);
}
.cart-row-title {
  flex: 1 1 auto;
  min-width: 0;
}
.cart-row-hint {
  flex: 1 1 100%;
  padding-left: 24px;
  font-size: 11.5px;
  color: var(--w-muted);
}
.cart-sync .shop-checkout {
  padding-top: 8px;
}
.shop-checkout-slot {
  flex: none;
}
/* Просмотрщик фото поверх страницы: затемнение, стрелки, крестик, счётчик.
   Лежит в Shadow DOM, но position: fixed позиционируется от вьюпорта — чужая
   вёрстка на него не влияет, а он на неё. z-index не нужен: сам хост виджета
   уже поднят на 2147483000. */
.lightbox {
  position: fixed;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 56px 16px;
  background: rgba(0, 0, 0, 0.88);
  cursor: zoom-out;
}
@media (prefers-reduced-motion: no-preference) {
  .lightbox {
    animation: widget-fade-in 0.18s ease;
  }
}
@keyframes widget-fade-in {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}
.lightbox-stage {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  max-width: 100%;
  max-height: 100%;
}
.lightbox-image {
  max-width: min(92vw, 1200px);
  max-height: 76vh;
  border-radius: 10px;
  object-fit: contain;
  cursor: default;
  background: #000;
}
.lightbox-caption {
  max-width: min(92vw, 640px);
  text-align: center;
  color: #fff;
  cursor: default;
}
.lightbox-title {
  font-size: 15px;
  font-weight: 600;
  line-height: 1.35;
}
.lightbox-meta {
  margin-top: 4px;
  font-size: 12.5px;
  line-height: 1.45;
  color: rgba(255, 255, 255, 0.75);
}
.lightbox-price {
  margin-top: 6px;
  font-size: 15px;
  font-weight: 700;
  /* На чёрном фоне бренд-фиолетовый тёмный — берём светлую версию токена. */
  color: #a99bff;
}
.lightbox-counter {
  position: absolute;
  top: 18px;
  left: 20px;
  color: rgba(255, 255, 255, 0.75);
  font-size: 13px;
  font-variant-numeric: tabular-nums;
}
.lightbox-close,
.lightbox-prev,
.lightbox-next {
  position: absolute;
  border: none;
  background: rgba(255, 255, 255, 0.12);
  color: #fff;
  border-radius: 999px;
  cursor: pointer;
  line-height: 1;
  /* 44px — минимальная зона нажатия на тач-экране (WCAG 2.5.5). */
  min-width: 44px;
  min-height: 44px;
  font-size: 22px;
}
.lightbox-close:hover,
.lightbox-prev:hover,
.lightbox-next:hover {
  background: rgba(255, 255, 255, 0.24);
}
.lightbox-close:focus-visible,
.lightbox-prev:focus-visible,
.lightbox-next:focus-visible {
  outline: 2px solid #fff;
  outline-offset: 2px;
}
.lightbox-close {
  top: 12px;
  right: 16px;
}
.lightbox-prev {
  left: 12px;
  font-size: 30px;
}
.lightbox-next {
  right: 12px;
  font-size: 30px;
}
/* Одно фото — листать нечего. */
.lightbox-single .lightbox-prev,
.lightbox-single .lightbox-next {
  display: none;
}
@media (max-width: 480px) {
  .lightbox {
    padding: 52px 8px;
  }
  .lightbox-image {
    max-height: 62vh;
  }
}
/* Подсветка рамки — движение цвета: под prefers-reduced-motion переход не
   включаем (§5), само наведение остаётся мгновенным. */
@media (prefers-reduced-motion: no-preference) {
  .gallery-card {
    transition: border-color 0.15s ease;
  }
}
/* Кнопки-действия под сообщением (столбик). */
.actions {
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.action-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  text-decoration: none;
  padding: 9px 12px;
  border-radius: 10px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  border: 1px solid var(--w-accent);
}
.action-btn.primary {
  background: var(--w-accent);
  color: #fff;
}
.action-btn.primary:hover {
  background: var(--w-accent-hover);
}
.action-btn.secondary {
  background: transparent;
  color: var(--w-accent);
}
.action-btn.secondary:hover {
  background: var(--w-msg-bg);
}
/* Чипы быстрых ответов (лента). */
.quick {
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
}
/* Чипы «Продолжить в Telegram/MAX» прижаты к НИЗУ ленты. В коротком диалоге всё
   свободное место уходит НАД ними (auto-отступ в колоночном флексе), поэтому они
   стоят у поля ввода, а не болтаются под приветствием с пустотой внизу. В
   длинном диалоге свободного места нет, отступ становится нулём — чипы просто
   идут за последней репликой (порядок держит keepContinueChipsLast). */
.quick.continue {
  margin-top: auto;
}
.quick-chip {
  border: 1px solid var(--w-input-border);
  background: var(--w-bg);
  color: var(--w-text);
  font: inherit;
  font-size: 11.5px;
  line-height: 1.3;
  padding: 4px 9px;
  border-radius: 999px;
  cursor: pointer;
  text-decoration: none;
}
.quick-chip:hover {
  border-color: var(--w-accent);
  color: var(--w-accent);
}
@media (max-width: 480px) {
  .panel {
    position: fixed;
    inset: 0;
    width: auto;
    height: auto;
    max-height: none;
    border-radius: 0;
    bottom: 0;
  }
  /* < 16px iOS зумит страницу при фокусе поля — на тач-экранах держим 16px
     (site_chat.css:562-565). Компенсируем max-height, чтобы не подрос composer. */
  .composer textarea {
    font-size: 16px;
    max-height: 120px;
  }
}
`;
